import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import { BASE_URL } from "../api/client";
const publicApi = axios.create({ baseURL: BASE_URL });
const BASE = BASE_URL;

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function placeholderGradient(nombre, color) {
  const angles = [135, 160, 110, 45, 200, 315, 180, 270];
  const hash = [...(nombre || "?")].reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
  const angle = angles[Math.abs(hash) % angles.length];
  return `linear-gradient(${angle}deg, ${color}ee 0%, ${color}77 100%)`;
}

function toBs(usd, tasa) {
  if (!tasa || usd == null) return null;
  return (Number(usd) * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ─── Carrito ─────────────────────────────────────────────────────────────── */
function useCarrito() {
  const [items, setItems] = useState([]);
  const makeUid = (prod, variantes) => {
    const vStr = Object.keys(variantes || {}).sort().map(k => `${k}:${variantes[k]}`).join("|");
    return `${prod.id}|${vStr}`;
  };
  const agregar = (prod, variantes = {}) =>
    setItems((prev) => {
      const uid = makeUid(prod, variantes);
      const ex = prev.find((i) => i._uid === uid);
      return ex
        ? prev.map((i) => (i._uid === uid ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { ...prod, _uid: uid, variantes: variantes || {}, qty: 1 }];
    });
  const quitar = (uid) =>
    setItems((prev) => {
      const ex = prev.find((i) => i._uid === uid);
      if (!ex) return prev;
      return ex.qty === 1 ? prev.filter((i) => i._uid !== uid) : prev.map((i) => (i._uid === uid ? { ...i, qty: i.qty - 1 } : i));
    });
  const vaciar = () => setItems([]);
  const total = items.reduce((s, i) => s + Number(i.precio_promocion || i.precio) * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  return { items, agregar, quitar, vaciar, total, count };
}

/* ─── Imagen de Producto (siempre cuadrada y limitada) ────────────────────── */
function ProductImg({ url, nombre, color, size = 160 }) {
  const inicial = (nombre || "?").trim().charAt(0).toUpperCase();
  const style = {
    width: "100%",
    height: size,
    objectFit: "cover",
    display: "block",
    flexShrink: 0,
  };
  if (url) {
    return <img src={`${BASE}${url}`} alt={nombre} style={style} />;
  }
  return (
    <div style={{
      ...style,
      background: placeholderGradient(nombre, color),
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <span style={{ fontSize: size * 0.35, fontWeight: 900, color: "rgba(255,255,255,0.85)", userSelect: "none" }}>
        {inicial}
      </span>
    </div>
  );
}

/* ─── Drawer del carrito ──────────────────────────────────────────────────── */
function CartDrawer({ open, carrito, onClose, onCheckout, color, tasa }) {
  if (!open) return null;
  const bs = tasa ? (carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 50, backdropFilter: "blur(2px)" }}
      />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 100vw)",
        background: "#fff", zIndex: 51, boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        animation: "slideInRight 0.25s ease",
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", margin: 0 }}>🛒 Tu pedido</h2>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 0" }}>{carrito.count} {carrito.count === 1 ? "producto" : "productos"}</p>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
          {carrito.items.length === 0 ? (
            <div style={{ textAlign: "center", paddingTop: 60, color: "#94a3b8" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛍️</div>
              <p style={{ fontWeight: 600, fontSize: 15 }}>Tu carrito está vacío</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Agrega productos para comenzar</p>
            </div>
          ) : (
            carrito.items.map((item) => {
              const precioUnit = Number(item.precio_promocion || item.precio);
              const subtotal = precioUnit * item.qty;
              const bsUnit = tasa ? toBs(precioUnit, tasa) : null;
              const varText = item.variantes && Object.keys(item.variantes).length > 0
                ? Object.entries(item.variantes).map(([k, v]) => `${k}: ${v}`).join(" · ")
                : null;
              return (
                <div key={item._uid} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid #f8fafc" }}>
                  <div style={{ width: 64, height: 64, borderRadius: 12, overflow: "hidden", flexShrink: 0, background: "#f1f5f9" }}>
                    <ProductImg url={item.foto_url} nombre={item.nombre} color={color} size={64} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.nombre}</p>
                    {varText && <p style={{ fontSize: 11, color, margin: "1px 0 0", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{varText}</p>}
                    <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 6px" }}>
                      ${precioUnit.toFixed(2)} c/u {bsUnit && <span>(Bs. {bsUnit})</span>}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={() => carrito.quitar(item._uid)} style={{ width: 26, height: 26, borderRadius: "50%", border: `1.5px solid ${color}`, background: "transparent", color, fontWeight: 900, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                      <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", minWidth: 18, textAlign: "center" }}>{item.qty}</span>
                      <button onClick={() => carrito.agregar(item, item.variantes)} style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", margin: 0 }}>${subtotal.toFixed(2)}</p>
                    {tasa && <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0" }}>Bs. {(subtotal * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {carrito.items.length > 0 && (
          <div style={{ padding: "16px 20px 24px", borderTop: "1px solid #f1f5f9", background: "#fafbfc" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "#64748b", fontSize: 14 }}>Subtotal</span>
              <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>${carrito.total.toFixed(2)}</span>
            </div>
            {bs && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>≈ Bs. {bs}</span>
              </div>
            )}
            <button
              onClick={onCheckout}
              style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 14, padding: "15px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: `0 4px 20px ${color}55`, transition: "opacity 0.15s" }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.9"}
              onMouseOut={e => e.currentTarget.style.opacity = "1"}
            >
              📱 Confirmar pedido por WhatsApp
            </button>
            <button onClick={carrito.vaciar} style={{ width: "100%", background: "transparent", border: "none", color: "#94a3b8", fontSize: 12, cursor: "pointer", marginTop: 10, padding: "4px 0" }}>
              Vaciar carrito
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Modal de variantes ─────────────────────────────────────────────────── */
function VarianteModal({ prod, color, onClose, onConfirm }) {
  const [sel, setSel] = useState({});
  useEffect(() => { setSel({}); }, [prod]);
  if (!prod) return null;
  const variantes = prod.variantes || [];
  const allSelected = variantes.length > 0 && variantes.every(v => sel[v.nombre]);
  const precio = Number(prod.precio_promocion || prod.precio);
  const precioOriginal = prod.precio_promocion ? Number(prod.precio) : null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 70, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, maxHeight: "82vh", overflowY: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.22)" }}>
        <div style={{ position: "sticky", top: 0, background: "#fff", padding: "18px 20px 12px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: "#0f172a", margin: 0 }}>{prod.nombre}</h3>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
              <span style={{ fontWeight: 900, fontSize: 20, color }}>${precio.toFixed(2)}</span>
              {precioOriginal && <span style={{ fontSize: 13, color: "#d1d5db", textDecoration: "line-through" }}>${precioOriginal.toFixed(2)}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 20, color: "#64748b" }}>×</button>
        </div>
        <div style={{ padding: "16px 20px 36px" }}>
          {variantes.map(v => (
            <div key={v.nombre} style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.07em" }}>{v.nombre}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(v.opciones || []).map(op => {
                  const active = sel[v.nombre] === op;
                  return (
                    <button key={op} onClick={() => setSel(s => ({ ...s, [v.nombre]: op }))} style={{
                      padding: "9px 20px", borderRadius: 22, fontSize: 14, fontWeight: 600, cursor: "pointer",
                      border: active ? `2px solid ${color}` : "1.5px solid #e2e8f0",
                      background: active ? color : "#fff",
                      color: active ? "#fff" : "#374151",
                      transition: "all 0.15s",
                      boxShadow: active ? `0 2px 12px ${color}44` : "none",
                    }}>{op}</button>
                  );
                })}
              </div>
            </div>
          ))}
          <button
            onClick={() => { if (allSelected) onConfirm(sel); }}
            disabled={!allSelected}
            style={{
              width: "100%", border: "none", borderRadius: 14, padding: "15px 0",
              fontWeight: 800, fontSize: 15, cursor: allSelected ? "pointer" : "not-allowed",
              background: allSelected ? color : "#e2e8f0",
              color: allSelected ? "#fff" : "#94a3b8",
              boxShadow: allSelected ? `0 4px 20px ${color}44` : "none",
              transition: "all 0.2s",
            }}
          >
            {allSelected ? "Agregar al carrito" : "Selecciona todas las opciones"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal detalle de producto ──────────────────────────────────────────── */
function ProductoDetalleModal({ prod, color, tasa, carrito, onClose }) {
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState({});
  useEffect(() => { setQty(1); setSel({}); }, [prod]);
  if (!prod) return null;

  const variantes = prod.variantes || [];
  const hasVariantes = variantes.length > 0;
  const allSelected = !hasVariantes || variantes.every(v => sel[v.nombre]);
  const precio = Number(prod.precio_promocion || prod.precio);
  const precioOriginal = prod.precio_promocion ? Number(prod.precio) : null;
  const descuento = precioOriginal ? Math.round((1 - precio / precioOriginal) * 100) : 0;

  const handleAgregar = () => {
    if (!allSelected) return;
    const vars = hasVariantes ? sel : {};
    for (let i = 0; i < qty; i++) carrito.agregar(prod, vars);
    onClose();
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 75, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 24, width: "100%", maxWidth: 780, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 28px 80px rgba(0,0,0,0.32)", display: "flex", flexWrap: "wrap", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.08)", cursor: "pointer", fontSize: 22, color: "#64748b", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>

        {/* Imagen */}
        <div style={{ flex: "1 1 260px", minHeight: 300, background: "#f8fafc", borderRadius: "24px 0 0 24px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {prod.foto_url ? (
            <img src={`${BASE}${prod.foto_url}`} alt={prod.nombre} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 300 }} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <svg width="88" height="88" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              <span style={{ fontSize: 12, color: "#cbd5e1", fontWeight: 600, letterSpacing: "0.04em" }}>Sin imagen</span>
            </div>
          )}
        </div>

        {/* Contenido */}
        <div style={{ flex: "2 1 280px", padding: "36px 36px 32px 28px", display: "flex", flexDirection: "column", minWidth: 0 }}>
          {prod._catNombre && (
            <span style={{ display: "inline-block", fontSize: 11, fontWeight: 800, color, background: `${color}18`, padding: "4px 14px", borderRadius: 20, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, alignSelf: "flex-start" }}>
              {prod._catNombre}
            </span>
          )}

          <h2 style={{ fontWeight: 900, fontSize: 22, color: "#0f172a", margin: "0 0 10px", lineHeight: 1.25, textTransform: "uppercase", letterSpacing: "-0.01em", paddingRight: 40 }}>
            {prod.nombre}
          </h2>

          <div style={{ marginBottom: tasa ? 4 : 16, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 900, fontSize: 34, color, lineHeight: 1 }}>${precio.toFixed(2)}</span>
            {precioOriginal && <span style={{ fontSize: 16, color: "#d1d5db", textDecoration: "line-through" }}>${precioOriginal.toFixed(2)}</span>}
            {descuento > 0 && <span style={{ fontSize: 11, background: "#ef4444", color: "#fff", padding: "3px 9px", borderRadius: 20, fontWeight: 800 }}>-{descuento}% OFF</span>}
          </div>
          {tasa && <div style={{ fontSize: 15, color: "#64748b", fontWeight: 700, marginBottom: 16 }}>Bs. {toBs(precio, tasa)}</div>}

          {prod.descripcion && (
            <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, margin: "0 0 20px" }}>{prod.descripcion}</p>
          )}

          {/* Variantes */}
          {hasVariantes && variantes.map(v => (
            <div key={v.nombre} style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>{v.nombre}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(v.opciones || []).map(op => {
                  const active = sel[v.nombre] === op;
                  return (
                    <button key={op} onClick={() => setSel(s => ({ ...s, [v.nombre]: op }))} style={{
                      padding: "7px 18px", borderRadius: 22, fontSize: 13, fontWeight: 600, cursor: "pointer",
                      border: active ? `2px solid ${color}` : "1.5px solid #e2e8f0",
                      background: active ? color : "#fff", color: active ? "#fff" : "#374151",
                      transition: "all 0.15s", boxShadow: active ? `0 2px 10px ${color}44` : "none",
                    }}>{op}</button>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{ borderTop: "1px solid #f1f5f9", marginTop: "auto", paddingTop: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>Cantidad</span>
              <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 42, height: 42, border: "none", background: "#f8fafc", color: "#374151", fontWeight: 900, fontSize: 20, cursor: "pointer" }}>−</button>
                <span style={{ width: 46, textAlign: "center", fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} style={{ width: 42, height: 42, border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 20, cursor: "pointer" }}>+</button>
              </div>
            </div>

            <button
              onClick={handleAgregar}
              disabled={!allSelected}
              style={{
                width: "100%", border: "none", borderRadius: 14, padding: "15px 0",
                fontWeight: 800, fontSize: 15, cursor: allSelected ? "pointer" : "not-allowed",
                background: allSelected ? color : "#e2e8f0",
                color: allSelected ? "#fff" : "#94a3b8",
                boxShadow: allSelected ? `0 4px 20px ${color}44` : "none",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 12, transition: "all 0.2s",
              }}
              onMouseOver={e => { if (allSelected) e.currentTarget.style.opacity = "0.9"; }}
              onMouseOut={e => e.currentTarget.style.opacity = "1"}
            >
              <span>🛒 Agregar al carrito</span>
              {allSelected && (
                <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 10, padding: "3px 10px", fontSize: 13 }}>
                  ${(precio * qty).toFixed(2)}{tasa ? ` / Bs. ${toBs(precio * qty, tasa)}` : ""}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Checkout Modal ──────────────────────────────────────────────────────── */
const LABELS_PAGO = {
  efectivo: "Efectivo",
  transferencia: "Transferencia bancaria",
  pago_movil: "Pago móvil",
  zelle: "Zelle",
  paypal: "PayPal",
  binance: "Binance Pay",
  tarjeta: "Tarjeta de crédito/débito",
};

function CheckoutModal({ open, carrito, subdominio, config, onClose, onSuccess }) {
  const [form, setForm] = useState({ cliente_nombre: "", cliente_telefono: "", cliente_direccion: "", notas: "" });
  const [tipoEntrega, setTipoEntrega] = useState("delivery");
  const [formaPago, setFormaPago] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tasa = config?.tasa_bcv && config.tasa_bcv > 0 ? config.tasa_bcv : null;

  if (!open) return null;

  // Solo las formas de pago activas en la configuración de la tienda
  const metodosPago = config?.formas_pago
    ? Object.entries(config.formas_pago).filter(([, v]) => v).map(([k]) => ({ key: k, label: LABELS_PAGO[k] || k }))
    : [];

  const construirMensaje = (pedidoId) => {
    const nombreTienda = config?.nombre_comercio || "";
    const lineas = carrito.items
      .map((i) => {
        const varText = i.variantes && Object.keys(i.variantes).length > 0
          ? ` (${Object.entries(i.variantes).map(([k, v]) => `${k}: ${v}`).join(", ")})`
          : "";
        return `• ${i.qty}x ${i.nombre}${varText}  $${(Number(i.precio_promocion || i.precio) * i.qty).toFixed(2)}`;
      })
      .join("\n");
    const entregaTexto = tipoEntrega === "delivery"
      ? `Delivery - ${form.cliente_direccion || "Sin direccion"}`
      : "Retiro en tienda";
    const pagoLabel = (metodosPago.find((m) => m.key === formaPago) || {}).label || formaPago || "No indicado";
    const bsTotal = tasa ? ` (Bs. ${(carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : "";
    const notasLinea = form.notas ? `\nObservaciones: ${form.notas}` : "";
    return [
      nombreTienda ? `Pedido en ${nombreTienda}` : "Nuevo pedido",
      `Numero de pedido: #${pedidoId}`,
      "",
      lineas,
      "",
      `Total: $${carrito.total.toFixed(2)}${bsTotal}`,
      `Entrega: ${entregaTexto}`,
      `Pago: ${pagoLabel}`,
      `Cliente: ${form.cliente_nombre}`,
      form.cliente_telefono ? `Telefono: ${form.cliente_telefono}` : "",
      notasLinea,
    ].filter((l) => l !== undefined).join("\n").replace(/\n{3,}/g, "\n\n").trim();
  };

  const enviarWhatsapp = (pedidoId) => {
    const wp = config?.whatsapp_config;
    const msg = construirMensaje(pedidoId);
    const encoded = encodeURIComponent(msg);
    const telefono = wp?.telefono ? wp.telefono.replace(/\D/g, "") : "";
    const url = telefono
      ? `https://wa.me/${telefono}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (metodosPago.length > 0 && !formaPago) { setError("Selecciona una forma de pago"); return; }
    setLoading(true);
    setError("");
    try {
      const pagoLabel = (metodosPago.find((m) => m.key === formaPago) || {}).label || formaPago;
      const notasParts = [pagoLabel ? `Pago: ${pagoLabel}` : "", tipoEntrega === "delivery" ? "" : "Retiro en tienda", form.notas].filter(Boolean);
      const payload = {
        cliente_nombre: form.cliente_nombre,
        cliente_telefono: form.cliente_telefono,
        cliente_direccion: tipoEntrega === "delivery" ? form.cliente_direccion : "Retiro en tienda",
        notas: notasParts.join(" | "),
        moneda: "USD",
        items: carrito.items.map((i) => ({ producto_id: i.id, cantidad: i.qty })),
      };
      const { data } = await publicApi.post(`/menu/${subdominio}/pedido`, payload);
      enviarWhatsapp(data.id);
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al procesar el pedido");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "11px 14px", fontSize: 14, outline: "none", boxSizing: "border-box", color: "#1e293b", transition: "border-color 0.15s", fontFamily: "inherit" };
  const focusColor = config?.color_tema || "#6366f1";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div style={{ position: "sticky", top: 0, background: "#fff", padding: "20px 20px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
          <h3 style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", margin: 0 }}>Confirmar pedido</h3>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 20, color: "#64748b" }}>×</button>
        </div>

        <div style={{ padding: "16px 20px 32px" }}>

          {/* Resumen */}
          <div style={{ background: "#f8fafc", borderRadius: 14, padding: 14, marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Resumen del pedido</p>
            {carrito.items.map((i) => {
              const subtotal = Number(i.precio_promocion || i.precio) * i.qty;
              return (
                <div key={i.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "#475569" }}>{i.qty}× {i.nombre}</span>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>${subtotal.toFixed(2)}</span>
                    {tasa && <span style={{ display: "block", fontSize: 10, color: "#94a3b8" }}>Bs. {(subtotal * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: "1px dashed #e2e8f0", marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>Total</span>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>${carrito.total.toFixed(2)}</span>
                {tasa && <span style={{ display: "block", fontSize: 11, color: "#64748b" }}>Bs. {(carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              </div>
            </div>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Tipo de entrega */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tipo de entrega</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { val: "delivery", icon: "🛵", label: "Delivery", sub: "Envío a domicilio" },
                  { val: "retiro", icon: "🏪", label: "Retiro", sub: "Paso a buscar" },
                ].map(({ val, icon, label, sub }) => {
                  const active = tipoEntrega === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setTipoEntrega(val)}
                      style={{
                        padding: "12px 10px", borderRadius: 14, cursor: "pointer", textAlign: "center",
                        border: active ? `2px solid ${focusColor}` : "2px solid #e2e8f0",
                        background: active ? `${focusColor}10` : "#fff",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ fontSize: 22, marginBottom: 2 }}>{icon}</div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: active ? focusColor : "#374151" }}>{label}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Datos del cliente */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Tu nombre *</label>
              <input
                type="text" required value={form.cliente_nombre}
                onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })}
                placeholder="¿Cómo te llamamos?"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = focusColor}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>WhatsApp / Teléfono</label>
              <input
                type="tel" value={form.cliente_telefono}
                onChange={(e) => setForm({ ...form, cliente_telefono: e.target.value })}
                placeholder="+584121234567"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = focusColor}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>

            {/* Dirección — solo si delivery */}
            {tipoEntrega === "delivery" && (
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Dirección de entrega *</label>
                <input
                  type="text" required={tipoEntrega === "delivery"}
                  value={form.cliente_direccion}
                  onChange={(e) => setForm({ ...form, cliente_direccion: e.target.value })}
                  placeholder="Calle, edificio, referencia..."
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = focusColor}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                />
              </div>
            )}

            {/* Forma de pago */}
            {metodosPago.length > 0 && (
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Forma de pago *</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {metodosPago.map(({ key, label }) => {
                    const active = formaPago === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormaPago(key)}
                        style={{
                          padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer",
                          border: active ? `2px solid ${focusColor}` : "1.5px solid #e2e8f0",
                          background: active ? focusColor : "#fff",
                          color: active ? "#fff" : "#374151",
                          transition: "all 0.15s",
                          boxShadow: active ? `0 2px 10px ${focusColor}44` : "none",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Observaciones</label>
              <textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Instrucciones especiales, talla, color, horario preferido..."
                rows={2}
                style={{ ...inputStyle, resize: "none" }}
                onFocus={e => e.target.style.borderColor = focusColor}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ background: "#25d366", color: "#fff", border: "none", borderRadius: 14, padding: "15px 0", fontWeight: 800, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, boxShadow: "0 4px 20px rgba(37,211,102,0.35)", marginTop: 4 }}
            >
              {loading ? "Procesando..." : "Enviar pedido por WhatsApp"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ─── Mapa de colores en español ──────────────────────────────────────────── */
const COLOR_MAP = {
  rojo: "#ef4444", azul: "#3b82f6", verde: "#22c55e", negro: "#1f2937",
  blanco: "#f9fafb", gris: "#9ca3af", amarillo: "#eab308", naranja: "#f97316",
  rosa: "#f472b6", morado: "#a855f7", celeste: "#38bdf8", marron: "#92400e",
  beige: "#d6ccc2", dorado: "#d97706", plateado: "#94a3b8", cafe: "#78350f",
  violeta: "#7c3aed", turquesa: "#06b6d4", salmon: "#fb7185", crema: "#fef3c7",
};
function colorHex(nombre) {
  return COLOR_MAP[nombre.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")] || null;
}

/* ─── Tarjeta de producto ─────────────────────────────────────────────────── */
function ProductCard({ prod, color, tasa, qty, onAdd, onRemove, hasVariantes = false, onClick }) {
  const precio = Number(prod.precio_promocion || prod.precio);
  const precioOriginal = prod.precio_promocion ? Number(prod.precio) : null;
  const descuento = precioOriginal ? Math.round((1 - precio / precioOriginal) * 100) : 0;
  const bs = toBs(precio, tasa);
  return (
    <div
      style={{ background: "#fff", borderRadius: 20, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", transition: "box-shadow 0.22s, transform 0.22s", cursor: "pointer" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,0.14)"; e.currentTarget.style.transform = "translateY(-5px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.07)"; e.currentTarget.style.transform = "translateY(0)"; }}
      onClick={onClick}
    >
      <div style={{ position: "relative", height: 210, overflow: "hidden", flexShrink: 0 }}>
        <ProductImg url={prod.foto_url} nombre={prod.nombre} color={color} size={210} />
        {descuento > 0 && (
          <div style={{ position: "absolute", top: 12, left: 12, background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "5px 11px", borderRadius: 20, boxShadow: "0 3px 12px rgba(239,68,68,0.5)" }}>
            -{descuento}% OFF
          </div>
        )}
        {qty > 0 && (
          <div style={{ position: "absolute", top: 12, right: 12, background: color, color: "#fff", width: 30, height: 30, borderRadius: "50%", fontSize: 13, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 12px ${color}77` }}>
            {qty}
          </div>
        )}
      </div>

      <div style={{ padding: "16px 16px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3 style={{ fontWeight: 800, fontSize: 14, color: "#111827", margin: "0 0 5px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {prod.nombre}
        </h3>
        {prod.descripcion && (
          <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 8px", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {prod.descripcion}
          </p>
        )}

        {/* Variantes disponibles */}
        {prod.variantes && prod.variantes.length > 0 && (
          <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 5 }}>
            {prod.variantes.map(v => {
              const esColor = v.nombre.toLowerCase().includes("color");
              return (
                <div key={v.nombre} style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>
                    {v.nombre}:
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {(v.opciones || []).map(op => {
                      const hex = esColor ? colorHex(op) : null;
                      return hex ? (
                        <span key={op} title={op} style={{
                          width: 16, height: 16, borderRadius: "50%", background: hex, flexShrink: 0,
                          border: "1.5px solid rgba(0,0,0,0.12)", display: "inline-block",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        }} />
                      ) : (
                        <span key={op} style={{
                          fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 8,
                          border: "1px solid #e5e7eb", color: "#6b7280", background: "#f9fafb",
                          lineHeight: 1.4,
                        }}>{op}</span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: "auto" }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
              <span style={{ fontWeight: 900, fontSize: 22, color, lineHeight: 1 }}>${precio.toFixed(2)}</span>
              {precioOriginal && <span style={{ fontSize: 13, color: "#d1d5db", textDecoration: "line-through" }}>${precioOriginal.toFixed(2)}</span>}
            </div>
            {bs && <span style={{ fontSize: 11, color: "#9ca3af", marginTop: 3, display: "block" }}>Bs. {bs}</span>}
          </div>
          {hasVariantes ? (
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 14, padding: "13px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: `0 6px 20px ${color}44`, transition: "opacity 0.15s" }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
              onMouseOut={e => e.currentTarget.style.opacity = "1"}
            >
              {qty > 0 ? `Agregar más (${qty} en carrito)` : "Seleccionar opciones"}
            </button>
          ) : qty === 0 ? (
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 14, padding: "13px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: `0 6px 20px ${color}44`, transition: "opacity 0.15s, transform 0.15s" }}
              onMouseOver={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "scale(0.98)"; }}
              onMouseOut={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1)"; }}
            >
              + Agregar al carrito
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", borderRadius: 14, overflow: "hidden", border: `2px solid ${color}33`, background: "#f8fafc" }}>
              <button onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{ flex: 1, height: 46, border: "none", background: "transparent", color, fontWeight: 900, fontSize: 24, cursor: "pointer" }}>−</button>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#111", minWidth: 36, textAlign: "center" }}>{qty}</span>
              <button onClick={(e) => { e.stopPropagation(); onAdd(); }} style={{ flex: 1, height: 46, border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 24, cursor: "pointer" }}>+</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Barra de búsqueda y categorías ─────────────────────────────────────── */
function BarraFiltros({ categorias, seleccionada, onSelect, busqueda, onBusqueda, color, theme = "light" }) {
  const dark = theme === "dark";
  const cream = theme === "cream";
  const inputBg = dark ? "rgba(255,255,255,0.09)" : cream ? "#f0ebe3" : "#f8fafc";
  const inputBorder = dark ? "rgba(255,255,255,0.14)" : cream ? "#d9cfc6" : "#e2e8f0";
  const inputColor = dark ? "#e2e8f0" : cream ? "#292524" : "#1e293b";
  const iconColor = dark ? "rgba(255,255,255,0.42)" : "#94a3b8";
  const chipBorder = dark ? "rgba(255,255,255,0.16)" : cream ? "#d9cfc6" : "#e2e8f0";
  const chipBg = dark ? "rgba(255,255,255,0.07)" : cream ? "rgba(0,0,0,0.04)" : "#fff";
  const chipColor = dark ? "rgba(255,255,255,0.55)" : cream ? "#78716c" : "#64748b";
  return (
    <div>
      <div style={{ padding: "10px 16px 8px" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, pointerEvents: "none", color: iconColor }}>🔍</span>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => onBusqueda(e.target.value)}
            placeholder="Buscar productos..."
            style={{ width: "100%", background: inputBg, border: `1.5px solid ${inputBorder}`, borderRadius: 12, padding: "9px 36px 9px 36px", fontSize: 13, color: inputColor, outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" }}
            onFocus={e => e.target.style.borderColor = color}
            onBlur={e => e.target.style.borderColor = inputBorder}
          />
          {busqueda && (
            <button onClick={() => onBusqueda("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: iconColor, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
          )}
        </div>
      </div>
      {categorias.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: "0 16px 10px", overflowX: "auto", scrollbarWidth: "none" }}>
          {[{ id: null, nombre: "Todos" }, ...categorias].map((cat) => {
            const active = seleccionada === (cat.id ?? null);
            return (
              <button
                key={cat.id ?? "all"}
                onClick={() => onSelect(cat.id ?? null)}
                style={{
                  flexShrink: 0, padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                  border: active ? "none" : `1.5px solid ${chipBorder}`,
                  background: active ? color : chipBg,
                  color: active ? "#fff" : chipColor,
                  boxShadow: active ? `0 2px 10px ${color}44` : "none",
                }}
              >
                {cat.nombre}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── CartFAB ─────────────────────────────────────────────────────────────── */
function CartFAB({ count, total, tasa, color, onClick }) {
  if (count === 0) return null;
  const bs = tasa ? (total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
  return (
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 35 }}>
      <button onClick={onClick} style={{ background: color, color: "#fff", border: "none", borderRadius: 50, padding: "14px 28px", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: `0 6px 28px ${color}55`, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 10 }}>
        <span>🛒 {count} {count === 1 ? "producto" : "productos"}</span>
        <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 10px" }}>${total.toFixed(2)}</span>
        {bs && <span style={{ fontSize: 11, opacity: 0.8 }}>· Bs. {bs}</span>}
      </button>
    </div>
  );
}

/* ─── Hero compartido ────────────────────────────────────────────────────── */
function Hero({ menu, color, nombre, carrito, onOpenCart, waveFill = "#f1f5f9" }) {
  return (
    <div style={{ position: "relative", background: color, overflow: "hidden" }}>
      {menu.config?.banner_url && (
        <>
          <img src={`${BASE}${menu.config.banner_url}`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}ee 0%, ${color}bb 100%)` }} />
        </>
      )}
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "52px 24px 68px" }}>
        {menu.config?.logo_url ? (
          <div style={{ width: 112, height: 112, borderRadius: "50%", overflow: "hidden", margin: "0 auto 20px", border: "4px solid rgba(255,255,255,0.95)", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
            <img src={`${BASE}${menu.config.logo_url}`} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
        ) : (
          <div style={{ width: 112, height: 112, borderRadius: "50%", background: "rgba(255,255,255,0.18)", border: "4px solid rgba(255,255,255,0.88)", margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
            <span style={{ fontSize: 50, fontWeight: 900, color: "#fff" }}>{nombre.charAt(0)}</span>
          </div>
        )}
        <h1 style={{ color: "#fff", fontWeight: 900, fontSize: 30, margin: "0 0 8px", textShadow: "0 2px 16px rgba(0,0,0,0.28)", letterSpacing: "-0.02em" }}>{nombre}</h1>
        {menu.config?.descripcion && (
          <p style={{ color: "rgba(255,255,255,0.88)", fontSize: 14, margin: "0 auto 26px", maxWidth: 360, lineHeight: 1.6 }}>{menu.config.descripcion}</p>
        )}
        <button
          onClick={onOpenCart}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.97)", color, border: "none", borderRadius: 50, padding: "13px 30px", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 6px 28px rgba(0,0,0,0.2)", transition: "transform 0.15s" }}
          onMouseOver={e => e.currentTarget.style.transform = "scale(1.04)"}
          onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
        >
          🛒 {carrito.count > 0 ? `${carrito.count} ítem${carrito.count !== 1 ? "s" : ""} · $${carrito.total.toFixed(2)}` : "Ver carrito"}
        </button>
      </div>
      <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
        <svg viewBox="0 0 1440 54" style={{ width: "100%", display: "block" }} preserveAspectRatio="none">
          <path d="M0,54 Q360,8 720,30 Q1080,52 1440,4 L1440,54 Z" fill={waveFill} />
        </svg>
      </div>
    </div>
  );
}

/* ─── PlantillaClasica ────────────────────────────────────────────────────── */
function PlantillaClasica({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Inter',system-ui,-apple-system,sans-serif" }}>
      <Hero menu={menu} color={color} nombre={nombre} carrito={carrito} onOpenCart={onOpenCart} waveFill="#f1f5f9" />
      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} />
      </div>
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 16px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "72px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div>
            <p style={{ fontWeight: 700, fontSize: 17 }}>Sin resultados</p>
          </div>
        ) : catsFiltradas.map((cat) => (
          <section key={cat.id} style={{ marginBottom: 44 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
              <div style={{ width: 5, height: 26, borderRadius: 3, background: color, flexShrink: 0 }} />
              <h2 style={{ fontWeight: 900, fontSize: 18, color: "#0f172a", margin: 0 }}>{cat.nombre}</h2>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              <span style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{cat.productos.length} producto{cat.productos.length !== 1 ? "s" : ""}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 18 }}>
              {cat.productos.map((prod) => {
                const hasV = prod.variantes && prod.variantes.length > 0;
                return (
                  <ProductCard key={prod.id} prod={prod} color={color} tasa={tasa} qty={qty(prod.id)} hasVariantes={hasV}
                    onAdd={() => hasV ? setDetalleProd({ ...prod, _catNombre: cat.nombre }) : carrito.agregar(prod)}
                    onRemove={() => carrito.quitar(`${prod.id}|`)}
                    onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })} />
                );
              })}
            </div>
          </section>
        ))}
      </main>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── PlantillaModerna ────────────────────────────────────────────────────── */
function PlantillaModerna({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* Hero oscuro */}
      <div style={{ position: "relative", background: "linear-gradient(160deg,#0d0d1a 0%,#1a1040 100%)", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${color}22 0%, transparent 70%)` }} />
        {menu.config?.banner_url && (
          <img src={`${BASE}${menu.config.banner_url}`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.18, display: "block" }} />
        )}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "52px 24px 68px" }}>
          {menu.config?.logo_url ? (
            <div style={{ width: 112, height: 112, borderRadius: "50%", overflow: "hidden", margin: "0 auto 20px", border: `4px solid ${color}`, boxShadow: `0 0 40px ${color}55, 0 10px 40px rgba(0,0,0,0.5)` }}>
              <img src={`${BASE}${menu.config.logo_url}`} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : (
            <div style={{ width: 112, height: 112, borderRadius: "50%", background: `${color}18`, border: `4px solid ${color}`, margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 40px ${color}44` }}>
              <span style={{ fontSize: 50, fontWeight: 900, color }}>{nombre.charAt(0)}</span>
            </div>
          )}
          <h1 style={{ color: "#fff", fontWeight: 900, fontSize: 30, margin: "0 0 8px", letterSpacing: "-0.02em", textShadow: `0 0 30px ${color}88` }}>{nombre}</h1>
          {menu.config?.descripcion && <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, margin: "0 auto 26px", maxWidth: 340, lineHeight: 1.6 }}>{menu.config.descripcion}</p>}
          <button onClick={onOpenCart} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: color, color: "#fff", border: "none", borderRadius: 50, padding: "13px 30px", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: `0 0 32px ${color}66` }}>
            🛒 {carrito.count > 0 ? `${carrito.count} ítem${carrito.count !== 1 ? "s" : ""} · $${carrito.total.toFixed(2)}` : "Ver carrito"}
          </button>
        </div>
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 54" style={{ width: "100%", display: "block" }} preserveAspectRatio="none">
            <path d="M0,54 Q360,8 720,30 Q1080,52 1440,4 L1440,54 Z" fill="#0d0d1a" />
          </svg>
        </div>
      </div>
      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(13,13,26,0.96)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${color}22` }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} theme="dark" />
      </div>
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 16px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "72px 0", color: "#374151" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div>
            <p style={{ fontWeight: 700, fontSize: 17, color: "#6b7280" }}>Sin resultados</p>
          </div>
        ) : catsFiltradas.map((cat) => (
          <section key={cat.id} style={{ marginBottom: 44 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
              <div style={{ flex: 1, height: 1, background: `${color}33` }} />
              <h2 style={{ fontWeight: 800, fontSize: 11, color, margin: 0, textTransform: "uppercase", letterSpacing: "0.22em" }}>{cat.nombre}</h2>
              <div style={{ flex: 1, height: 1, background: `${color}33` }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 18 }}>
              {cat.productos.map((prod) => {
                const precio = Number(prod.precio_promocion || prod.precio);
                const bs = toBs(precio, tasa);
                const q = qty(prod.id);
                const hasV = prod.variantes && prod.variantes.length > 0;
                return (
                  <div key={prod.id} style={{ background: "#13131f", borderRadius: 20, overflow: "hidden", border: `1px solid ${color}1a`, transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 16px 48px rgba(0,0,0,0.5), 0 0 24px ${color}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })}>
                    <div style={{ height: 200, overflow: "hidden", position: "relative" }}>
                      <ProductImg url={prod.foto_url} nombre={prod.nombre} color={color} size={200} />
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
                      {prod.precio_promocion && <div style={{ position: "absolute", top: 12, left: 12, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, padding: "4px 10px", borderRadius: 20 }}>PROMO</div>}
                    </div>
                    <div style={{ padding: "14px 16px 16px" }}>
                      <h3 style={{ fontWeight: 700, fontSize: 14, color: "#e2e8f0", margin: "0 0 4px", lineHeight: 1.4 }}>{prod.nombre}</h3>
                      {prod.descripcion && <p style={{ fontSize: 12, color: "#4b5563", margin: "0 0 12px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{prod.descripcion}</p>}
                      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 }}>
                        <div>
                          <span style={{ fontWeight: 900, fontSize: 20, color }}>${precio.toFixed(2)}</span>
                          {bs && <span style={{ fontSize: 11, color: "#4b5563", display: "block", marginTop: 2 }}>Bs. {bs}</span>}
                        </div>
                        {hasV ? (
                          <button onClick={(e) => { e.stopPropagation(); setDetalleProd({ ...prod, _catNombre: cat.nombre }); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 12, padding: "9px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", boxShadow: `0 4px 16px ${color}55`, whiteSpace: "nowrap" }}>
                            {q > 0 ? `+Agregar (${q})` : "Opciones"}
                          </button>
                        ) : q === 0 ? (
                          <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 12, padding: "9px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: `0 4px 16px ${color}55`, whiteSpace: "nowrap" }}>+ Agregar</button>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, background: `${color}18`, borderRadius: 12, padding: "4px 8px" }}>
                            <button onClick={(e) => { e.stopPropagation(); carrito.quitar(`${prod.id}|`); }} style={{ width: 28, height: 28, borderRadius: 8, border: "none", background: `${color}33`, color, fontWeight: 900, fontSize: 16, cursor: "pointer" }}>−</button>
                            <span style={{ color: "#fff", fontWeight: 900, fontSize: 15, minWidth: 20, textAlign: "center" }}>{q}</span>
                            <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 28, height: 28, borderRadius: 8, border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer" }}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── PlantillaElegante ───────────────────────────────────────────────────── */
function PlantillaElegante({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  return (
    <div style={{ minHeight: "100vh", background: "#faf8f5", fontFamily: "Georgia,'Times New Roman',serif" }}>
      {/* Hero elegante: fondo crema con acento de color */}
      <div style={{ position: "relative", background: `linear-gradient(160deg, ${color} 0%, ${color}cc 100%)`, overflow: "hidden" }}>
        {menu.config?.banner_url && (
          <>
            <img src={`${BASE}${menu.config.banner_url}`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: `${color}dd` }} />
          </>
        )}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "52px 24px 72px" }}>
          {menu.config?.logo_url ? (
            <div style={{ width: 112, height: 112, borderRadius: "50%", overflow: "hidden", margin: "0 auto 22px", border: "4px solid rgba(255,255,255,0.95)", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
              <img src={`${BASE}${menu.config.logo_url}`} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : (
            <div style={{ width: 112, height: 112, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "4px solid rgba(255,255,255,0.85)", margin: "0 auto 22px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 50, fontWeight: 700, color: "#fff", fontFamily: "Georgia,serif" }}>{nombre.charAt(0)}</span>
            </div>
          )}
          <h1 style={{ color: "#fff", fontWeight: 700, fontSize: 28, margin: "0 0 6px", letterSpacing: "0.12em", textTransform: "uppercase" }}>{nombre}</h1>
          {menu.config?.descripcion && <p style={{ color: "rgba(255,255,255,0.82)", fontSize: 14, margin: "0 auto 26px", maxWidth: 320, fontStyle: "italic", lineHeight: 1.6 }}>{menu.config.descripcion}</p>}
          <button onClick={onOpenCart} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.97)", color, border: "none", borderRadius: 50, padding: "12px 28px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Georgia,serif", boxShadow: "0 6px 28px rgba(0,0,0,0.18)" }}>
            {carrito.count > 0 ? `Pedido (${carrito.count}) · $${carrito.total.toFixed(2)}` : "Ver mi pedido"}
          </button>
        </div>
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 54" style={{ width: "100%", display: "block" }} preserveAspectRatio="none">
            <path d="M0,54 Q360,8 720,30 Q1080,52 1440,4 L1440,54 Z" fill="#faf8f5" />
          </svg>
        </div>
      </div>

      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "#faf8f5", borderBottom: "1px solid #e8e0d8" }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} theme="cream" />
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 16px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <p style={{ textAlign: "center", color: "#a8a29e", padding: "56px 0", fontStyle: "italic", fontSize: 15 }}>Sin resultados</p>
        ) : catsFiltradas.map((cat) => (
          <div key={cat.id} style={{ marginBottom: 36 }}>
            <div style={{ textAlign: "center", margin: "0 0 18px" }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.3em", color: "#a8a29e", textTransform: "uppercase", margin: "0 0 8px" }}>{cat.nombre}</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <div style={{ height: 1, flex: 1, maxWidth: 80, background: "#e0d8d0" }} />
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                <div style={{ height: 1, flex: 1, maxWidth: 80, background: "#e0d8d0" }} />
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", border: "1px solid #f0ebe3" }}>
              {cat.productos.map((prod, i) => {
                const precio = Number(prod.precio_promocion || prod.precio);
                const bs = toBs(precio, tasa);
                const q = qty(prod.id);
                const hasV = prod.variantes && prod.variantes.length > 0;
                return (
                  <div key={prod.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", borderBottom: i < cat.productos.length - 1 ? "1px solid #f5f0eb" : "none", cursor: "pointer" }}
                    onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })}>
                    <div style={{ width: 68, height: 68, borderRadius: 14, overflow: "hidden", flexShrink: 0, border: "1px solid #ece8e0" }}>
                      <ProductImg url={prod.foto_url} nombre={prod.nombre} color={color} size={68} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontWeight: 700, fontSize: 15, color: "#292524", margin: "0 0 3px" }}>{prod.nombre}</h3>
                      {prod.descripcion && <p style={{ fontSize: 12, color: "#a8a29e", margin: 0, fontStyle: "italic", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{prod.descripcion}</p>}
                    </div>
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color }}>${precio.toFixed(2)}</div>
                      {bs && <div style={{ fontSize: 10, color: "#a8a29e" }}>Bs. {bs}</div>}
                      <div style={{ marginTop: 8 }}>
                        {hasV ? (
                          <button onClick={(e) => { e.stopPropagation(); setDetalleProd({ ...prod, _catNombre: cat.nombre }); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 20, padding: "7px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Georgia,serif", boxShadow: `0 3px 12px ${color}44` }}>
                            {q > 0 ? `Agregar (${q})` : "Opciones"}
                          </button>
                        ) : q === 0 ? (
                          <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 20, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "Georgia,serif", boxShadow: `0 3px 12px ${color}44` }}>Agregar</button>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <button onClick={(e) => { e.stopPropagation(); carrito.quitar(`${prod.id}|`); }} style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px solid ${color}`, background: "#fff", color, fontWeight: 900, cursor: "pointer", fontSize: 16 }}>−</button>
                            <span style={{ fontWeight: 800, minWidth: 20, textAlign: "center", color: "#292524", fontSize: 15 }}>{q}</span>
                            <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, cursor: "pointer", fontSize: 16 }}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── PlantillaColorida ───────────────────────────────────────────────────── */
function PlantillaColorida({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  const catColors = ["#FF6B6B","#4ECDC4","#45B7D1","#FF8C42","#A855F7","#10B981","#F59E0B","#EC4899"];
  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(145deg, ${color} 0%, ${color}99 60%, #1a1a2e 100%)`, fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* Hero vibrante */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        {menu.config?.banner_url && (
          <img src={`${BASE}${menu.config.banner_url}`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.22, display: "block" }} />
        )}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "52px 24px 80px" }}>
          {menu.config?.logo_url ? (
            <div style={{ width: 120, height: 120, borderRadius: "50%", overflow: "hidden", margin: "0 auto 20px", border: "5px solid rgba(255,255,255,0.95)", boxShadow: "0 12px 48px rgba(0,0,0,0.3), 0 0 0 8px rgba(255,255,255,0.12)" }}>
              <img src={`${BASE}${menu.config.logo_url}`} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : (
            <div style={{ width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "5px solid rgba(255,255,255,0.9)", margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.25), 0 0 0 8px rgba(255,255,255,0.1)" }}>
              <span style={{ fontSize: 54, fontWeight: 900, color: "#fff" }}>{nombre.charAt(0)}</span>
            </div>
          )}
          <h1 style={{ color: "#fff", fontWeight: 900, fontSize: 32, margin: "0 0 8px", letterSpacing: "-0.02em", textShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>{nombre}</h1>
          {menu.config?.descripcion && <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, margin: "0 auto 28px", maxWidth: 340, lineHeight: 1.6 }}>{menu.config.descripcion}</p>}
          <button onClick={onOpenCart} style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "#fff", color, border: "none", borderRadius: 50, padding: "14px 32px", fontWeight: 900, fontSize: 15, cursor: "pointer", boxShadow: "0 8px 32px rgba(0,0,0,0.22)" }}>
            🛒 {carrito.count > 0 ? `${carrito.count} ítem${carrito.count !== 1 ? "s" : ""} · $${carrito.total.toFixed(2)}` : "Ver carrito"}
          </button>
        </div>
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 60" style={{ width: "100%", display: "block" }} preserveAspectRatio="none">
            <path d="M0,60 Q480,10 960,40 Q1200,55 1440,15 L1440,60 Z" fill="rgba(0,0,0,0.15)" />
          </svg>
        </div>
      </div>
      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(255,255,255,0.96)", backdropFilter: "blur(12px)", boxShadow: "0 2px 16px rgba(0,0,0,0.14)" }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} />
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 14px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <div style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", borderRadius: 24, padding: "56px 0", textAlign: "center", color: "#fff" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div><p style={{ fontWeight: 700, fontSize: 17 }}>Sin resultados</p>
          </div>
        ) : catsFiltradas.map((cat, ci) => {
          const cc = catColors[ci % catColors.length];
          return (
            <div key={cat.id} style={{ marginBottom: 36 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fff", boxShadow: `0 0 0 3px ${cc}, 0 4px 12px rgba(0,0,0,0.3)` }} />
                <h2 style={{ fontWeight: 900, fontSize: 20, color: "#fff", margin: 0, textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>{cat.nombre}</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16 }}>
                {cat.productos.map((prod) => {
                  const precio = Number(prod.precio_promocion || prod.precio);
                  const bs = toBs(precio, tasa);
                  const q = qty(prod.id);
                  const hasV = prod.variantes && prod.variantes.length > 0;
                  return (
                    <div key={prod.id} style={{ background: "#fff", borderRadius: 22, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.02)"; e.currentTarget.style.boxShadow = "0 20px 56px rgba(0,0,0,0.3)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.2)"; }}
                      onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })}>
                      <div style={{ height: 180, overflow: "hidden", position: "relative" }}>
                        <ProductImg url={prod.foto_url} nombre={prod.nombre} color={cc} size={180} />
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: cc }} />
                        {prod.precio_promocion && <div style={{ position: "absolute", top: 12, left: 12, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 900, padding: "4px 10px", borderRadius: 20 }}>OFERTA</div>}
                      </div>
                      <div style={{ padding: "12px 14px 14px" }}>
                        <h3 style={{ fontWeight: 800, fontSize: 13, color: "#111", margin: "0 0 4px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{prod.nombre}</h3>
                        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 6, marginTop: 8 }}>
                          <div>
                            <span style={{ fontWeight: 900, fontSize: 18, color: cc }}>${precio.toFixed(2)}</span>
                            {bs && <span style={{ fontSize: 10, color: "#9ca3af", display: "block" }}>Bs. {bs}</span>}
                          </div>
                          {hasV ? (
                            <button onClick={(e) => { e.stopPropagation(); setDetalleProd({ ...prod, _catNombre: cat.nombre }); }} style={{ background: cc, color: "#fff", border: "none", borderRadius: 12, padding: "8px 12px", fontWeight: 800, fontSize: 12, cursor: "pointer", boxShadow: `0 4px 14px ${cc}55`, whiteSpace: "nowrap" }}>
                              {q > 0 ? `+Agregar (${q})` : "Opciones"}
                            </button>
                          ) : q === 0 ? (
                            <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ background: cc, color: "#fff", border: "none", borderRadius: 12, padding: "8px 14px", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: `0 4px 14px ${cc}55`, whiteSpace: "nowrap" }}>+ Agregar</button>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <button onClick={(e) => { e.stopPropagation(); carrito.quitar(`${prod.id}|`); }} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: `${cc}22`, color: cc, fontWeight: 900, fontSize: 18, cursor: "pointer" }}>−</button>
                              <span style={{ fontWeight: 900, fontSize: 15, minWidth: 20, textAlign: "center" }}>{q}</span>
                              <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: cc, color: "#fff", fontWeight: 900, fontSize: 18, cursor: "pointer" }}>+</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── PlantillaDelivery ───────────────────────────────────────────────────── */
function PlantillaDelivery({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  return (
    <div style={{ minHeight: "100vh", background: "#f2f3f7", fontFamily: "'Inter',system-ui,sans-serif" }}>
      <Hero menu={menu} color={color} nombre={nombre} carrito={carrito} onOpenCart={onOpenCart} waveFill="#f2f3f7" />
      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} />
      </div>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 12px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "72px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div><p style={{ fontWeight: 700, fontSize: 17 }}>Sin resultados</p>
          </div>
        ) : catsFiltradas.map((cat) => (
          <div key={cat.id} style={{ marginBottom: 30 }}>
            {catsFiltradas.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 4, height: 20, borderRadius: 2, background: color }} />
                <h2 style={{ fontWeight: 900, fontSize: 16, color: "#111", margin: 0 }}>{cat.nombre}</h2>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
              {cat.productos.map((prod) => {
                const precio = Number(prod.precio_promocion || prod.precio);
                const bs = toBs(precio, tasa);
                const q = qty(prod.id);
                const hasV = prod.variantes && prod.variantes.length > 0;
                return (
                  <div key={prod.id} style={{ position: "relative", borderRadius: 20, overflow: "hidden", aspectRatio: "1/1", background: "#e5e7eb", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(0,0,0,0.22)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
                    onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })}>
                    <div style={{ position: "absolute", inset: 0 }}>
                      {prod.foto_url ? (
                        <img src={`${BASE}${prod.foto_url}`} alt={prod.nombre} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      ) : (
                        <div style={{ width: "100%", height: "100%", background: placeholderGradient(prod.nombre, color), display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: "3.5rem", fontWeight: 900, color: "rgba(255,255,255,0.35)" }}>{prod.nombre.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    {prod.precio_promocion && <div style={{ position: "absolute", top: 10, left: 10, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 900, padding: "4px 9px", borderRadius: 20, zIndex: 2 }}>OFERTA</div>}
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top,rgba(0,0,0,0.88) 0%,rgba(0,0,0,0.55) 50%,transparent 100%)", padding: "28px 12px 12px", zIndex: 2 }}>
                      <p style={{ color: "#fff", fontWeight: 700, fontSize: 13, margin: "0 0 6px", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{prod.nombre}</p>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                        <div>
                          <span style={{ color: "#fff", fontWeight: 900, fontSize: 14 }}>${precio.toFixed(2)}</span>
                          {bs && <span style={{ color: "rgba(255,255,255,0.65)", fontSize: 10, display: "block" }}>Bs. {bs}</span>}
                        </div>
                        {hasV ? (
                          <button onClick={(e) => { e.stopPropagation(); setDetalleProd({ ...prod, _catNombre: cat.nombre }); }} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: q > 0 ? 13 : 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 14px ${color}66` }}>
                            {q > 0 ? q : "+"}
                          </button>
                        ) : q === 0 ? (
                          <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 14px ${color}66` }}>+</button>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(0,0,0,0.35)", borderRadius: 20, padding: "3px 8px" }}>
                            <button onClick={(e) => { e.stopPropagation(); carrito.quitar(`${prod.id}|`); }} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer" }}>−</button>
                            <span style={{ color: "#fff", fontWeight: 900, fontSize: 14, minWidth: 14, textAlign: "center" }}>{q}</span>
                            <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer" }}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── PlantillaAcordeon ───────────────────────────────────────────────────── */
function PlantillaAcordeon({ menu, color, tasa, catsFiltradas, carrito, busqueda, setBusqueda, catSeleccionada, setCatSeleccionada, onOpenCart }) {
  const qty = (id) => carrito.items.filter((i) => i.id === id).reduce((s, i) => s + i.qty, 0);
  const nombre = menu.config?.nombre_comercio || menu.tenant.nombre_tienda;
  const [detalleProd, setDetalleProd] = useState(null);
  const [abiertos, setAbiertos] = useState(() => new Set((menu.categorias || []).map((c) => c.id)));
  const toggleCat = (id) => setAbiertos((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  return (
    <div style={{ minHeight: "100vh", background: "#f4f4f6", fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* Hero de marca */}
      <div style={{ position: "relative", background: color, overflow: "hidden" }}>
        {menu.config?.banner_url && (
          <>
            <img src={`${BASE}${menu.config.banner_url}`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: `${color}dd` }} />
          </>
        )}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "50px 24px 72px" }}>
          {menu.config?.logo_url ? (
            <div style={{ width: 116, height: 116, borderRadius: "50%", overflow: "hidden", margin: "0 auto 20px", border: "5px solid rgba(255,255,255,0.95)", boxShadow: "0 12px 48px rgba(0,0,0,0.28)" }}>
              <img src={`${BASE}${menu.config.logo_url}`} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : (
            <div style={{ width: 116, height: 116, borderRadius: "50%", background: "rgba(255,255,255,0.16)", border: "5px solid rgba(255,255,255,0.88)", margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.22)" }}>
              <span style={{ color: "#fff", fontSize: 52, fontWeight: 900 }}>{nombre.charAt(0)}</span>
            </div>
          )}
          <h1 style={{ color: "#fff", fontWeight: 900, fontSize: 30, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px", textShadow: "0 3px 16px rgba(0,0,0,0.28)" }}>{nombre}</h1>
          {menu.config?.descripcion && <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, margin: "0 auto 26px", maxWidth: 340, lineHeight: 1.6, fontStyle: "italic" }}>{menu.config.descripcion}</p>}
          <button onClick={onOpenCart} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.97)", color, border: "none", borderRadius: 50, padding: "13px 30px", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 6px 28px rgba(0,0,0,0.22)" }}>
            🛒 {carrito.count > 0 ? `${carrito.count} ítem${carrito.count !== 1 ? "s" : ""} · $${carrito.total.toFixed(2)}` : "Ver carrito"}
          </button>
        </div>
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 54" style={{ width: "100%", display: "block" }} preserveAspectRatio="none">
            <path d="M0,54 Q360,8 720,30 Q1080,52 1440,4 L1440,54 Z" fill="#f4f4f6" />
          </svg>
        </div>
      </div>

      <div style={{ position: "sticky", top: 0, zIndex: 40, background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <BarraFiltros categorias={menu.categorias} seleccionada={catSeleccionada} onSelect={setCatSeleccionada} busqueda={busqueda} onBusqueda={setBusqueda} color={color} />
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 14px 110px" }}>
        {catsFiltradas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "56px 0", color: "#9ca3af" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <p style={{ fontWeight: 700, fontSize: 16 }}>Sin resultados</p>
          </div>
        ) : catsFiltradas.map((cat) => {
          const open = busqueda ? true : abiertos.has(cat.id);
          return (
            <div key={cat.id} style={{ background: "#fff", borderRadius: 20, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", marginBottom: 14 }}>
              <button
                onClick={() => toggleCat(cat.id)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "transparent", border: "none", borderLeft: `6px solid ${color}`, cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 900, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.1em", color }}>{cat.nombre}</span>
                  <span style={{ fontSize: 11, color: "#9ca3af", background: "#f3f4f6", borderRadius: 20, padding: "2px 8px" }}>{cat.productos.length}</span>
                </div>
                <span style={{ color: "#9ca3af", fontSize: 20, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s", display: "block", lineHeight: 1 }}>▾</span>
              </button>
              {open && cat.productos.map((prod, i) => {
                const precio = Number(prod.precio_promocion || prod.precio);
                const bs = toBs(precio, tasa);
                const q = qty(prod.id);
                const hasV = prod.variantes && prod.variantes.length > 0;
                return (
                  <div key={prod.id} style={{ display: "flex", gap: 14, padding: "14px 20px", borderTop: "1px solid #f3f4f6", alignItems: "center", background: i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                    onClick={() => setDetalleProd({ ...prod, _catNombre: cat.nombre })}>
                    <div style={{ width: 68, height: 68, borderRadius: 14, overflow: "hidden", flexShrink: 0, background: "#f3f4f6" }}>
                      <ProductImg url={prod.foto_url} nombre={prod.nombre} color={color} size={68} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontWeight: 700, fontSize: 14, color: "#111", margin: "0 0 3px" }}>{prod.nombre}</h3>
                      {prod.descripcion && <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 5px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{prod.descripcion}</p>}
                      <div>
                        <span style={{ fontWeight: 900, fontSize: 15, color }}>${precio.toFixed(2)}</span>
                        {bs && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 7 }}>Bs. {bs}</span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {hasV ? (
                        <button onClick={(e) => { e.stopPropagation(); setDetalleProd({ ...prod, _catNombre: cat.nombre }); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 12, padding: "9px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: `0 4px 14px ${color}44` }}>
                          {q > 0 ? `Agregar (${q})` : "Opciones"}
                        </button>
                      ) : q === 0 ? (
                        <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ background: color, color: "#fff", border: "none", borderRadius: 12, padding: "9px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: `0 4px 14px ${color}44` }}>Agregar</button>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 7, background: "#f3f4f6", borderRadius: 12, padding: "4px 8px" }}>
                          <button onClick={(e) => { e.stopPropagation(); carrito.quitar(`${prod.id}|`); }} style={{ width: 30, height: 30, borderRadius: 9, border: "none", background: `${color}18`, color, fontWeight: 900, fontSize: 18, cursor: "pointer" }}>−</button>
                          <span style={{ fontWeight: 900, minWidth: 20, textAlign: "center", color: "#111", fontSize: 15 }}>{q}</span>
                          <button onClick={(e) => { e.stopPropagation(); carrito.agregar(prod); }} style={{ width: 30, height: 30, borderRadius: 9, border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 18, cursor: "pointer" }}>+</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <CartFAB count={carrito.count} total={carrito.total} tasa={tasa} color={color} onClick={onOpenCart} />
      <ProductoDetalleModal prod={detalleProd} color={color} tasa={tasa} carrito={carrito} onClose={() => setDetalleProd(null)} />
    </div>
  );
}

/* ─── Página principal ────────────────────────────────────────────────────── */
export default function MenuPublico() {
  const { subdominio } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [exito, setExito] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [catSeleccionada, setCatSeleccionada] = useState(null);
  const [tasasBCV, setTasasBCV] = useState(null);
  const carrito = useCarrito();

  useEffect(() => {
    publicApi.get(`/menu/${subdominio}`)
      .then((r) => setMenu(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    publicApi.get("/tasas/ultima")
      .then((r) => setTasasBCV(r.data))
      .catch(() => {});
  }, [subdominio]);

  if (loading)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, border: "3px solid #e2e8f0", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: "#94a3b8", fontSize: 14, marginTop: 14 }}>Cargando catálogo...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>😕</div>
          <h1 style={{ fontWeight: 800, fontSize: 20, color: "#1e293b", margin: "0 0 8px" }}>Catálogo no encontrado</h1>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>El enlace puede ser incorrecto o el catálogo no existe.</p>
        </div>
      </div>
    );

  if (exito)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0fdf4", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 340 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <h1 style={{ fontWeight: 800, fontSize: 24, color: "#166534", margin: "0 0 10px" }}>¡Pedido confirmado!</h1>
          <p style={{ color: "#4ade80", fontSize: 15, marginBottom: 28 }}>Pronto el negocio se pondrá en contacto contigo.</p>
          <button
            onClick={() => { setExito(null); carrito.vaciar(); setBusqueda(""); setCatSeleccionada(null); }}
            style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 14, padding: "14px 32px", fontWeight: 800, fontSize: 15, cursor: "pointer" }}
          >
            Seguir comprando
          </button>
        </div>
      </div>
    );

  const color = menu.config?.color_tema || "#6366f1";
  const monedaBCV = menu.config?.moneda_bcv;
  const tasa = monedaBCV === "eur"    ? (tasasBCV?.eur || null)
             : monedaBCV === "usd"    ? (tasasBCV?.usd || null)
             : monedaBCV === "manual" ? (menu.config?.tasa_bcv || null)
             : null;

  const catsFiltradas = (menu.categorias || [])
    .filter((cat) => catSeleccionada === null || cat.id === catSeleccionada)
    .map((cat) => ({
      ...cat,
      productos: cat.productos.filter((p) =>
        !busqueda ||
        p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        (p.descripcion || "").toLowerCase().includes(busqueda.toLowerCase())
      ),
    }))
    .filter((cat) => cat.productos.length > 0);

  const PLANTILLAS = {
    clasica: PlantillaClasica,
    moderna: PlantillaModerna,
    elegante: PlantillaElegante,
    colorida: PlantillaColorida,
    delivery: PlantillaDelivery,
    acordeon: PlantillaAcordeon,
  };
  const Plantilla = PLANTILLAS[menu.config?.plantilla] || PlantillaClasica;

  const props = {
    menu, color, tasa, catsFiltradas, carrito,
    busqueda, setBusqueda, catSeleccionada, setCatSeleccionada,
    onOpenCart: () => setDrawerOpen(true),
  };

  return (
    <>
      <Plantilla {...props} />

      <CartDrawer
        open={drawerOpen}
        carrito={carrito}
        color={color}
        tasa={tasa}
        onClose={() => setDrawerOpen(false)}
        onCheckout={() => { setDrawerOpen(false); setCheckout(true); }}
      />
      <CheckoutModal
        open={checkout}
        carrito={carrito}
        subdominio={subdominio}
        config={menu.config}
        onClose={() => setCheckout(false)}
        onSuccess={(pedido) => { setCheckout(false); setExito(pedido); }}
      />
    </>
  );
}
